if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.restaurantScale = 1;
        this.restaurantBaseScale = 1;
        this.restaurantWheelTimeout = null;
        this.restaurantControlsBound = false;
        this.restaurantDragging = false;
        this.restaurantDragPointerId = null;
        this.restaurantDragStartX = 0;
        this.restaurantDragStartY = 0;
        this.restaurantScrollStartX = 0;
        this.restaurantScrollStartY = 0;

        this.onRestaurantWheel = this.onRestaurantWheel.bind(this);
        this.onRestaurantPointerDown = this.onRestaurantPointerDown.bind(this);
        this.onRestaurantPointerMove = this.onRestaurantPointerMove.bind(this);
        this.onRestaurantPointerUp = this.onRestaurantPointerUp.bind(this);
        this.onRestaurantZoomIn = () => this.adjustRestaurantZoom(1.15);
        this.onRestaurantZoomOut = () => this.adjustRestaurantZoom(1 / 1.15);
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.classList.contains('product-media-modal--restaurant')) {
          this.bindRestaurantControls();
        }
      }

      disconnectedCallback() {
        this.unbindRestaurantControls();
      }

      isRestaurantLightbox() {
        return this.classList.contains('product-media-modal--restaurant');
      }

      bindRestaurantControls() {
        if (this.restaurantControlsBound || !this.isRestaurantLightbox()) return;

        this.restaurantViewer = this.querySelector('.product-media-modal__viewer');
        this.restaurantZoomInButton = this.querySelector('.product-media-modal__zoom-in');
        this.restaurantZoomOutButton = this.querySelector('.product-media-modal__zoom-out');
        this.restaurantToolbar = this.querySelector('.product-media-modal__toolbar');

        this.restaurantZoomInButton?.addEventListener('click', this.onRestaurantZoomIn);
        this.restaurantZoomOutButton?.addEventListener('click', this.onRestaurantZoomOut);
        this.restaurantViewer?.addEventListener('wheel', this.onRestaurantWheel, { passive: false });
        this.restaurantViewer?.addEventListener('pointerdown', this.onRestaurantPointerDown);
        this.restaurantViewer?.addEventListener('pointermove', this.onRestaurantPointerMove);
        this.restaurantViewer?.addEventListener('pointerup', this.onRestaurantPointerUp);
        this.restaurantViewer?.addEventListener('pointercancel', this.onRestaurantPointerUp);

        this.restaurantControlsBound = true;
      }

      unbindRestaurantControls() {
        if (!this.restaurantControlsBound) return;

        this.restaurantZoomInButton?.removeEventListener('click', this.onRestaurantZoomIn);
        this.restaurantZoomOutButton?.removeEventListener('click', this.onRestaurantZoomOut);
        this.restaurantViewer?.removeEventListener('wheel', this.onRestaurantWheel);
        this.restaurantViewer?.removeEventListener('pointerdown', this.onRestaurantPointerDown);
        this.restaurantViewer?.removeEventListener('pointermove', this.onRestaurantPointerMove);
        this.restaurantViewer?.removeEventListener('pointerup', this.onRestaurantPointerUp);
        this.restaurantViewer?.removeEventListener('pointercancel', this.onRestaurantPointerUp);

        this.restaurantControlsBound = false;
      }

      hide() {
        this.endRestaurantDrag();
        this.resetRestaurantZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });

        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        if (this.isRestaurantLightbox()) {
          const container = this.querySelector('[role="document"]');
          container?.scrollTo({ left: 0, top: 0 });
          this.updateRestaurantToolbar(activeMedia);

          if (
            (activeMedia.nodeName === 'DEFERRED-MEDIA' || activeMedia.nodeName === 'PRODUCT-MODEL') &&
            typeof activeMedia.loadContent === 'function'
          ) {
            activeMedia.loadContent();
          } else {
            this.resetRestaurantZoom();
          }
          return;
        }

        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      getActiveRestaurantImage() {
        return this.querySelector('.product-media-modal__content > img.active');
      }

      updateRestaurantToolbar(activeMedia) {
        if (!this.restaurantToolbar) return;

        const isImage = activeMedia?.tagName === 'IMG';
        this.restaurantToolbar.hidden = !isImage;
      }

      resetRestaurantZoom() {
        const activeImage = this.getActiveRestaurantImage();
        if (!activeImage || !this.restaurantViewer) {
          this.restaurantScale = 1;
          this.restaurantBaseScale = 1;
          this.updateRestaurantZoomButtons();
          this.updateRestaurantPanState();
          return;
        }

        if (!activeImage.complete) {
          activeImage.addEventListener('load', () => this.resetRestaurantZoom(), { once: true });
          return;
        }

        requestAnimationFrame(() => {
          if (!this.hasAttribute('open')) return;

          const containerWidth = this.restaurantViewer.clientWidth || activeImage.naturalWidth;
          this.restaurantBaseScale = containerWidth / activeImage.naturalWidth;
          this.restaurantScale = this.restaurantBaseScale;
          this.renderRestaurantImage(activeImage);
          this.updateRestaurantZoomButtons();
          this.updateRestaurantPanState();
        });
      }

      renderRestaurantImage(image) {
        if (!image) return;

        const width = image.naturalWidth * this.restaurantScale;
        const height = image.naturalHeight * this.restaurantScale;

        image.style.width = `${width}px`;
        image.style.height = `${height}px`;
      }

      adjustRestaurantZoom(factor) {
        const activeImage = this.getActiveRestaurantImage();
        if (!activeImage || !factor) return;

        const minScale = this.restaurantBaseScale * 0.5;
        const maxScale = this.restaurantBaseScale * 3;
        const nextScale = Math.min(maxScale, Math.max(minScale, this.restaurantScale * factor));

        if (Math.abs(nextScale - this.restaurantScale) < 0.001) return;

        this.restaurantScale = nextScale;
        this.renderRestaurantImage(activeImage);
        this.updateRestaurantZoomButtons();
        this.updateRestaurantPanState();
      }

      updateRestaurantZoomButtons() {
        const minScale = this.restaurantBaseScale * 0.5;
        const maxScale = this.restaurantBaseScale * 3;

        if (this.restaurantZoomOutButton) {
          this.restaurantZoomOutButton.disabled = this.restaurantScale <= minScale + 0.001;
        }

        if (this.restaurantZoomInButton) {
          this.restaurantZoomInButton.disabled = this.restaurantScale >= maxScale - 0.001;
        }
      }

      isRestaurantPanEnabled() {
        return this.restaurantScale > this.restaurantBaseScale + 0.001;
      }

      updateRestaurantPanState() {
        if (!this.restaurantViewer) return;

        if (this.isRestaurantPanEnabled()) {
          this.restaurantViewer.classList.add('restaurant-pdf-lightbox__viewer--pannable');
        } else {
          this.restaurantViewer.classList.remove('restaurant-pdf-lightbox__viewer--pannable');
          this.restaurantViewer.scrollLeft = 0;
          this.restaurantViewer.scrollTop = 0;
        }
      }

      onRestaurantWheel(event) {
        if (!this.hasAttribute('open') || !this.getActiveRestaurantImage()) return;

        event.preventDefault();
        const delta = event.deltaY > 0 ? 1 / 1.1 : 1.1;

        clearTimeout(this.restaurantWheelTimeout);
        this.restaurantWheelTimeout = setTimeout(() => {
          this.adjustRestaurantZoom(delta);
        }, 16);
      }

      onRestaurantPointerDown(event) {
        if (!this.isRestaurantPanEnabled() || event.button !== 0 || !this.restaurantViewer) return;

        this.restaurantDragging = true;
        this.restaurantDragPointerId = event.pointerId;
        this.restaurantDragStartX = event.clientX;
        this.restaurantDragStartY = event.clientY;
        this.restaurantScrollStartX = this.restaurantViewer.scrollLeft;
        this.restaurantScrollStartY = this.restaurantViewer.scrollTop;

        this.restaurantViewer.setPointerCapture(event.pointerId);
        this.restaurantViewer.classList.add('restaurant-pdf-lightbox__viewer--dragging');
      }

      onRestaurantPointerMove(event) {
        if (!this.restaurantDragging || event.pointerId !== this.restaurantDragPointerId || !this.restaurantViewer) {
          return;
        }

        event.preventDefault();

        const deltaX = event.clientX - this.restaurantDragStartX;
        const deltaY = event.clientY - this.restaurantDragStartY;

        this.restaurantViewer.scrollLeft = this.restaurantScrollStartX - deltaX;
        this.restaurantViewer.scrollTop = this.restaurantScrollStartY - deltaY;
      }

      onRestaurantPointerUp(event) {
        if (!this.restaurantDragging || event.pointerId !== this.restaurantDragPointerId) return;
        this.endRestaurantDrag();
      }

      endRestaurantDrag() {
        if (!this.restaurantViewer) return;

        if (
          this.restaurantDragging &&
          this.restaurantDragPointerId !== null &&
          this.restaurantViewer.hasPointerCapture(this.restaurantDragPointerId)
        ) {
          this.restaurantViewer.releasePointerCapture(this.restaurantDragPointerId);
        }

        this.restaurantDragging = false;
        this.restaurantDragPointerId = null;
        this.restaurantViewer.classList.remove('restaurant-pdf-lightbox__viewer--dragging');
      }
    }
  );
}
